import { UIEventHandler } from 'react';
import WS from 'webstudio/src/client/facade';
import input from 'webstudio/src/input';
import { CAM_MOUSE_PAN_MULT, CAM_MOUSE_ZOOM_MULT, CAM_ROT_VELOCITY } from '../constants';
import {
  isLeftButtonPressed,
  isMiddleButtonPressed,
  isRightButtonPressed,
  isMouseZooming,
  isCtrlPressed,
} from '../helpers';

const savedPosition = new Array(2);
let manipulatorIsActive = false;
let controlIsActive = false;
let cursorIsLocked = false;
let mouseOutHandlerWrapper;
let mouseOverHandlerWrapper;
let mouseMoveHandlerWrapper;
let mouseUpHandlerWrapper;
let mouseDownHandlerWrapper;
let doubleClickHandlerWrapper;
let wheelHandlerWrapper;
let touchEndHandlerWrapper;
let gestureStartHandlerWrapper;
let gestureChangeHandlerWrapper;
let studioTouchStartHandlerWrapper;
let studioGectureStartHandlerWrapper;
let isTouch = false;

function onContextMenu(e) {
  e.preventDefault();
  e.stopPropagation();
}

function cloudNavigationMouseDownHandler(e) {
  input.onMouseDown(e.clientX, e.clientY, e.button, e.buttons);
  if (isTouch) {
    return;
  }
  if (isRightButtonPressed(e) || isMiddleButtonPressed(e)) {
    input.controlStart(e.clientX, e.clientY);
  } else if (isLeftButtonPressed(e)) {
    input.manipulatorStart(e.clientX, e.clientY);
  }
}

function cloudNavigationMouseMoveHandler(e) {
  if (input.onMouseMove) {
    input.onMouseMove(e.clientX, e.clientY, e.button, e.buttons);
  }

  if (isTouch) {
    return;
  }
  if (e.touches && e.touches > 1) {
    return;
  }

  if (isRightButtonPressed(e)) {
    WS.onPan = true;
    input.controlPan(CAM_MOUSE_PAN_MULT * e.movementX, CAM_MOUSE_PAN_MULT * e.movementY);
  } else if (isMiddleButtonPressed(e) || (cursorIsLocked && isLeftButtonPressed(e))) {
    input.controlMove(CAM_ROT_VELOCITY * e.movementX, CAM_ROT_VELOCITY * e.movementY);
  } else {
    input.manipulatorMove(e.clientX, e.clientY, isLeftButtonPressed(e));
  }
}

function cloudNavigationMouseUpHandler(e) {
  input.onMouseUp(e.clientX, e.clientY, e.button, e.buttons);
  if (isTouch) {
    isTouch = false;
    return;
  }
  if (e.button === 0) {
    input.manipulatorEnd(e.clientX, e.clientY);
  } else if (e.button === 1) {
    input.controlEnd();
    input.manipulatorMove(e.clientX, e.clientY, isLeftButtonPressed(e));
  } else if (e.button === 2) {
    WS.enablePointPopup(e.clientX, e.clientY);
    input.controlEnd();
    input.manipulatorMove(e.clientX, e.clientY, isLeftButtonPressed(e));
  }
}

const studioNavigationMouseDownHandler = (erasingOrSmoothingToolActive: boolean) => e => {
  input.onMouseDown(e.clientX, e.clientY, e.button, e.buttons);
  if (isTouch) {
    return;
  }

  if (isMiddleButtonPressed(e)) {
    input.controlStart(e.clientX, e.clientY);

    return;
  }

  if (isLeftButtonPressed(e)) {
    if (erasingOrSmoothingToolActive && isCtrlPressed(e)) {
      input.manipulatorStart(e.clientX, e.clientY);

      return;
    }

    input.controlStart(e.clientX, e.clientY);
  }
};

const studioNavigationMouseMoveHandler = (erasingOrSmoothingToolActive: boolean) => e => {
  if (input.onMouseMove) {
    input.onMouseMove(e.clientX, e.clientY, e.button, e.buttons);
  }

  if (isTouch) {
    return;
  }
  if (e.touches && e.touches > 1) {
    return;
  }

  const ctrlOrMetaPressed = isCtrlPressed(e);

  if (isMiddleButtonPressed(e)) {
    WS.onPan = true;
    input.controlPan(CAM_MOUSE_PAN_MULT * e.movementX, CAM_MOUSE_PAN_MULT * e.movementY);
  } else if (
    (isLeftButtonPressed(e) &&
      (!erasingOrSmoothingToolActive || (erasingOrSmoothingToolActive && !ctrlOrMetaPressed))) ||
    (cursorIsLocked && isRightButtonPressed(e))
  ) {
    input.controlMove(CAM_ROT_VELOCITY * e.movementX, CAM_ROT_VELOCITY * e.movementY);
  } else {
    input.manipulatorMove(
      e.clientX,
      e.clientY,
      isLeftButtonPressed(e) && (!erasingOrSmoothingToolActive || (erasingOrSmoothingToolActive && ctrlOrMetaPressed))
    );
  }
};

const studioNavigationMouseUpHandler = (erasingOrSmoothingToolActive: boolean) => e => {
  input.onMouseUp(e.clientX, e.clientY, e.button, e.buttons);
  if (isTouch) {
    isTouch = false;
    return;
  }

  const ctrlOrMetaPressed = isCtrlPressed(e);
  const manipulatorActive =
    isLeftButtonPressed(e) && (!erasingOrSmoothingToolActive || (erasingOrSmoothingToolActive && ctrlOrMetaPressed));

  if (e.button === 0) {
    input.manipulatorEnd(e.clientX, e.clientY);
  } else if (e.button === 1) {
    input.controlEnd();
    input.manipulatorMove(e.clientX, e.clientY, manipulatorActive);
  } else if (e.button === 2) {
    WS.enablePointPopup(e.clientX, e.clientY);
    input.controlEnd();
    input.manipulatorMove(e.clientX, e.clientY, manipulatorActive);
  }
};

let mouseZooming;
let mouseDefinedTimeout;

function mouseWheelHandler(e) {
  if (isTouch) {
    return;
  }

  e.preventDefault();
  e.stopImmediatePropagation();

  if (isLeftButtonPressed(e) || isRightButtonPressed(e)) {
    return;
  }

  clearTimeout(mouseDefinedTimeout);
  mouseDefinedTimeout = setTimeout(() => {
    mouseZooming = undefined;
    input.controlEnd();
  }, 1000);

  if (typeof mouseZooming === 'undefined') {
    mouseZooming = isMouseZooming(e);
  }

  const delta = (e.deltaY > 0 ? 1.0 : -1.0) * CAM_MOUSE_ZOOM_MULT;

  if (e.ctrlKey || e.metaKey || mouseZooming === true) {
    input.controlZoom(e.clientX, e.clientY, delta * 1.5);
  } else {
    const { clientX, clientY } = e;
    if (mouseZooming === false) {
      input.controlStart(clientX, clientY);
      input.controlMove(CAM_ROT_VELOCITY * -e.deltaX, CAM_ROT_VELOCITY * -e.deltaY);
    }
  }
}

let scale = 1;
let lastGestureScale = 1;
let gestureOn = false;

const onGestureChange = e => {
  if (isTouch) {
    return;
  }
  e.preventDefault();
  gestureOn = true;

  if (e.type === 'gesturestart') {
    lastGestureScale = e.scale;
  }

  scale = e.scale - lastGestureScale;
  input.controlZoom(e.clientX, e.clientY, -scale);
  lastGestureScale = e.scale;
};

function mouseDoubleClickHandler(e) {
  input.controlSetTarget(e.clientX, e.clientY);
}

const cloudNavigationMouseOutHandler = canvas => e => {
  const x = Math.max(0, Math.min(canvas.clientWidth, e.clientX));
  const y = Math.max(0, Math.min(canvas.clientHeight, e.clientY));

  savedPosition[0] = x;
  savedPosition[1] = y;

  input.manipulatorMove(x, y, isLeftButtonPressed(e));

  if (isLeftButtonPressed(e)) {
    input.manipulatorMove(x, y, true);
    manipulatorIsActive = true;
  } else {
    input.manipulatorMove(-99999, -99999, false);
  }

  if (isMiddleButtonPressed(e)) {
    controlIsActive = true;
  }
};

const studioNavigationMouseOutHandler = (canvas: HTMLCanvasElement, erasingOrSmoothingToolActive: boolean) => e => {
  const x = Math.max(0, Math.min(canvas.clientWidth, e.clientX));
  const y = Math.max(0, Math.min(canvas.clientHeight, e.clientY));

  savedPosition[0] = x;
  savedPosition[1] = y;

  const ctrlOrMetaPressed = isCtrlPressed(e);
  const manipulatorActive =
    isLeftButtonPressed(e) && (!erasingOrSmoothingToolActive || (erasingOrSmoothingToolActive && ctrlOrMetaPressed));

  input.manipulatorMove(x, y, manipulatorActive);

  if (manipulatorActive) {
    input.manipulatorMove(x, y, true);
    manipulatorIsActive = manipulatorActive;
  } else {
    input.manipulatorMove(-99999, -99999, false);
  }

  if (isMiddleButtonPressed(e)) {
    controlIsActive = true;
  }
};

const cloudNavigationMouseOverHandler = (canvas: HTMLCanvasElement) => e => {
  let x = e.clientX;
  let y = e.clientY;

  x = savedPosition[0] === 0 ? 0 : x;
  x = savedPosition[0] === canvas.clientWidth ? canvas.clientWidth : x;
  y = savedPosition[1] === 0 ? 0 : y;
  y = savedPosition[1] === canvas.clientHeight ? canvas.clientHeight : y;

  if (isLeftButtonPressed(e)) {
    if (manipulatorIsActive) {
      input.manipulatorMove(x, y, true);
    } else {
      input.manipulatorStart(x, y);
    }
  } else if (manipulatorIsActive) {
    input.manipulatorEnd(x, y);
  }

  if (isMiddleButtonPressed(e)) {
    if (controlIsActive) {
      input.controlMove(CAM_ROT_VELOCITY * e.movementX, CAM_ROT_VELOCITY * e.movementY);
    } else {
      input.controlStart(x, y);
    }
  } else if (controlIsActive) {
    input.controlEnd();
  }
};

const studioNavigationMouseOverHandler = (canvas: HTMLCanvasElement, erasingOrSmoothingToolActive: boolean) => e => {
  let x = e.clientX;
  let y = e.clientY;

  x = savedPosition[0] === 0 ? 0 : x;
  x = savedPosition[0] === canvas.clientWidth ? canvas.clientWidth : x;
  y = savedPosition[1] === 0 ? 0 : y;
  y = savedPosition[1] === canvas.clientHeight ? canvas.clientHeight : y;

  if ((isLeftButtonPressed(e) && !erasingOrSmoothingToolActive) || (erasingOrSmoothingToolActive && isCtrlPressed(e))) {
    if (manipulatorIsActive) {
      input.manipulatorMove(x, y, true);
    } else {
      input.manipulatorStart(x, y);
    }
  } else if (manipulatorIsActive) {
    input.manipulatorEnd(x, y);
  }

  if (isMiddleButtonPressed(e)) {
    if (controlIsActive) {
      input.controlMove(CAM_ROT_VELOCITY * e.movementX, CAM_ROT_VELOCITY * e.movementY);
    } else {
      input.controlStart(x, y);
    }
  } else if (controlIsActive) {
    input.controlEnd();
  }
};

function onCursorLocked({ x, y }) {
  input.viewcubeLockCursor(x, y);
  cursorIsLocked = true;
}

function onCursorUnlocked() {
  input.controlEnd();
  cursorIsLocked = false;
}

const onKeyDown = () => {
  //
};

const onKeyUp = () => {
  //
};

let prevX = -1;
let prevY = -1;
let prevX1 = -1;
let prevY1 = -1;
let prevX2 = -1;
let prevY2 = -1;
let prevDistance = 0;

function touchDownHandler(e) {
  isTouch = true;

  if (e.touches.length === 0) {
    return;
  }

  const touch = e.targetTouches && e.targetTouches.length ? e.targetTouches[0] : null;
  if (!touch) {
    return;
  }
  if (e.touches.length === 1) {
    const clientX = Math.floor(touch.clientX);
    const clientY = Math.floor(touch.clientY);
    prevX = clientX;
    prevY = clientY;
    input.manipulatorStart(clientX, clientY);
  } else if (e.touches.length === 2) {
    // calc medium
    const { clientX: x1, clientY: y1 } = e.touches[0];
    const { clientX: x2, clientY: y2 } = e.touches[1];
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;
    input.controlStart(centerX, centerY);
  } else {
    const { clientX: x1, clientY: y1 } = e.touches[0];
    const { clientX: x2, clientY: y2 } = e.touches[1];
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;
    input.controlStart(centerX, centerY);
  }
}

let touchTimeout: number | undefined;
const touchMoveHandler = e => {
  const touch = e.targetTouches?.length ? e.targetTouches[0] : null;
  if (!touch) {
    return;
  }

  clearTimeout(touchTimeout);
  touchTimeout = window.setTimeout(() => {
    input.controlEnd();
  }, 1000);

  if (e.touches.length === 3) {
    const { clientX: x1, clientY: y1 } = e.touches[0];
    const { clientX: x2, clientY: y2 } = e.touches[1];

    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    if (prevX2 < 0) {
      prevX2 = centerX;
    }
    if (prevY2 < 0) {
      prevY2 = centerY;
    }

    const movementX2 = centerX - prevX2;
    const movementY2 = centerY - prevY2;

    if (Math.abs(movementX2) > 65 || Math.abs(movementX2) > 65) {
      return;
    }

    input.controlStart(centerX, centerY);
    input.controlPan(CAM_MOUSE_PAN_MULT * movementX2, CAM_MOUSE_PAN_MULT * movementY2);

    prevX2 = centerX;
    prevY2 = centerY;
  } else if (e.touches.length === 2) {
    const { clientX: x1, clientY: y1 } = e.touches[0];
    const { clientX: x2, clientY: y2 } = e.touches[1];
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    if (prevX1 < 0) {
      prevX1 = centerX;
    }
    if (prevY1 < 0) {
      prevY1 = centerY;
    }

    const a = x1 - x2;
    const b = y1 - y2;

    const distance = Math.sqrt(a * a + b * b);

    if (prevDistance !== 0 && !gestureOn) {
      input.controlZoom(centerX, centerY, (distance - prevDistance) / -200);
    }

    const movementX1 = centerX - prevX1;
    const movementY1 = centerY - prevY1;

    if (Math.abs(movementX1) > 50 || Math.abs(movementY1) > 50) {
      return;
    }

    input.controlStart(centerX, centerY);
    input.controlMove(CAM_ROT_VELOCITY * movementX1, CAM_ROT_VELOCITY * movementY1);

    prevX1 = centerX;
    prevY1 = centerY;
    prevDistance = distance;
  } else if (e.touches.length === 1) {
    const clientX = Math.floor(touch.clientX);
    const clientY = Math.floor(touch.clientY);

    if (prevX < 0) {
      prevX = clientX;
    }
    if (prevY < 0) {
      prevY = clientY;
    }

    //	input.manipulatorStart(clientX, clientY);
    input.manipulatorMove(clientX, clientY, true);
    //	input.manipulatorEnd(clientX, clientY);

    prevX = clientX;
    prevY = clientY;
  }
};

function touchUpHandler(e) {
  if (e.touches?.length === 0) {
    const { clientX, clientY } = e.changedTouches[0];
    input.manipulatorEnd(clientX, clientY);
    prevX = -1;
    prevY = -1;
    prevX1 = -1;
    prevY1 = -1;
    prevX2 = -1;
    prevY2 = -1;
    prevDistance = 0;
  }
}

const resetHelperVariables = () => {
  manipulatorIsActive = false;
  controlIsActive = false;
  cursorIsLocked = false;
  mouseMoveHandlerWrapper = undefined;
  mouseOutHandlerWrapper = undefined;
  mouseOverHandlerWrapper = undefined;
  wheelHandlerWrapper = undefined;
  touchEndHandlerWrapper = undefined;
  mouseUpHandlerWrapper = undefined;
  mouseDownHandlerWrapper = undefined;
  doubleClickHandlerWrapper = undefined;
  gestureStartHandlerWrapper = undefined;
  gestureChangeHandlerWrapper = undefined;
  studioTouchStartHandlerWrapper = undefined;
  studioGectureStartHandlerWrapper = undefined;
  isTouch = false;
  mouseZooming = undefined;
  mouseDefinedTimeout = undefined;
  touchTimeout = undefined;
  scale = 1;
  lastGestureScale = 1;
  gestureOn = false;
  prevX = -1;
  prevY = -1;
  prevX1 = -1;
  prevY1 = -1;
  prevX2 = -1;
  prevY2 = -1;
  prevDistance = 0;
};

const wrappedHandler = <T extends () => void>(handler: UIEventHandler, effectCallback?: T) =>
  typeof effectCallback === 'function'
    ? e => {
        effectCallback();
        handler(e);
      }
    : handler;

interface IAddPointerEventsParams<T extends () => void> {
  canvas: HTMLCanvasElement;
  onUserInteraction?: T;
  onDoubleClick?: T;
}

interface IAddStudioPointerEventsParams<T extends () => void> extends IAddPointerEventsParams<T> {
  onGestureOrTouch?: T;
  erasingOrSmoothingToolActive?: boolean;
}

export const addCloudPointerEvents = <T extends () => void>({
  canvas,
  onUserInteraction,
  onDoubleClick,
}: IAddPointerEventsParams<T>) => {
  mouseOutHandlerWrapper = cloudNavigationMouseOutHandler(canvas);
  mouseOverHandlerWrapper = cloudNavigationMouseOverHandler(canvas);
  wheelHandlerWrapper = wrappedHandler(mouseWheelHandler, onUserInteraction);
  touchEndHandlerWrapper = wrappedHandler(touchUpHandler, onUserInteraction);
  mouseUpHandlerWrapper = wrappedHandler(cloudNavigationMouseUpHandler, onUserInteraction);
  gestureStartHandlerWrapper = wrappedHandler(onGestureChange, onUserInteraction);
  gestureChangeHandlerWrapper = wrappedHandler(onGestureChange, onUserInteraction);
  doubleClickHandlerWrapper = wrappedHandler(mouseDoubleClickHandler, onDoubleClick);

  canvas.addEventListener('touchstart', touchDownHandler, false);
  canvas.addEventListener('touchend', touchUpHandler, false);
  canvas.addEventListener('touchcancel', touchEndHandlerWrapper, false);
  canvas.addEventListener('touchmove', touchMoveHandler, false);
  canvas.addEventListener('wheel', wheelHandlerWrapper, false);
  canvas.addEventListener('mousedown', cloudNavigationMouseDownHandler, false);
  canvas.addEventListener('mousemove', cloudNavigationMouseMoveHandler, false);
  canvas.addEventListener('mouseup', mouseUpHandlerWrapper, false);
  canvas.addEventListener('dblclick', doubleClickHandlerWrapper, false);
  canvas.addEventListener('mouseout', mouseOutHandlerWrapper, false);
  canvas.addEventListener('mouseover', mouseOverHandlerWrapper, false);
  canvas.addEventListener('gesturestart', gestureStartHandlerWrapper, false);
  canvas.addEventListener('gesturechange', gestureChangeHandlerWrapper, false);
  canvas.addEventListener('gestureend', gestureChangeHandlerWrapper, false);
  canvas.addEventListener('contextmenu', onContextMenu, false);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);

  WS.on({ event: 'lock_cursor', callback: onCursorLocked });
  WS.on({ event: 'unlock_cursor', callback: onCursorUnlocked });
};

export function removeCloudPointerEvents(canvas: HTMLCanvasElement) {
  canvas.removeEventListener('touchstart', touchDownHandler);
  canvas.removeEventListener('touchend', touchUpHandler);
  canvas.removeEventListener('touchcancel', touchEndHandlerWrapper);
  canvas.removeEventListener('touchmove', touchMoveHandler);
  canvas.removeEventListener('wheel', wheelHandlerWrapper);
  canvas.removeEventListener('mousedown', cloudNavigationMouseDownHandler);
  canvas.removeEventListener('mousemove', cloudNavigationMouseMoveHandler);
  canvas.removeEventListener('mouseup', mouseUpHandlerWrapper);
  canvas.removeEventListener('dblclick', doubleClickHandlerWrapper);
  canvas.removeEventListener('mouseout', mouseOutHandlerWrapper);
  canvas.removeEventListener('mouseover', mouseOverHandlerWrapper);
  canvas.removeEventListener('gesturestart', gestureStartHandlerWrapper);
  canvas.removeEventListener('gesturechange', gestureChangeHandlerWrapper);
  canvas.removeEventListener('gestureend', gestureChangeHandlerWrapper);
  canvas.removeEventListener('contextmenu', onContextMenu);
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('keyup', onKeyUp);

  WS.off({ event: 'lock_cursor', callback: onCursorLocked });
  WS.off({ event: 'unlock_cursor', callback: onCursorUnlocked });

  resetHelperVariables();
}

export const addStudioPointerEvents = <T extends () => void>({
  canvas,
  onGestureOrTouch,
  onUserInteraction,
  onDoubleClick,
  erasingOrSmoothingToolActive = false,
}: IAddStudioPointerEventsParams<T>) => {
  mouseMoveHandlerWrapper = studioNavigationMouseMoveHandler(erasingOrSmoothingToolActive);
  mouseOutHandlerWrapper = studioNavigationMouseOutHandler(canvas, erasingOrSmoothingToolActive);
  mouseOverHandlerWrapper = studioNavigationMouseOverHandler(canvas, erasingOrSmoothingToolActive);
  mouseUpHandlerWrapper = wrappedHandler(
    studioNavigationMouseUpHandler(erasingOrSmoothingToolActive),
    onUserInteraction
  );
  mouseDownHandlerWrapper = studioNavigationMouseDownHandler(erasingOrSmoothingToolActive);
  wheelHandlerWrapper = wrappedHandler(mouseWheelHandler, onUserInteraction);
  doubleClickHandlerWrapper = wrappedHandler(mouseDoubleClickHandler, onDoubleClick);

  studioGectureStartHandlerWrapper = () => {
    gestureOn = true;
    onGestureOrTouch?.();
  };

  studioTouchStartHandlerWrapper = () => {
    isTouch = true;
    onGestureOrTouch?.();
  };

  canvas.addEventListener('touchstart', studioTouchStartHandlerWrapper);
  canvas.addEventListener('touchmove', studioTouchStartHandlerWrapper);
  canvas.addEventListener('gesturestart', studioGectureStartHandlerWrapper);
  canvas.addEventListener('gesturechange', studioGectureStartHandlerWrapper);
  canvas.addEventListener('wheel', wheelHandlerWrapper, false);
  canvas.addEventListener('mousedown', mouseDownHandlerWrapper, false);
  canvas.addEventListener('mousemove', mouseMoveHandlerWrapper, false);
  canvas.addEventListener('mouseup', mouseUpHandlerWrapper, false);
  canvas.addEventListener('dblclick', doubleClickHandlerWrapper, false);
  canvas.addEventListener('mouseout', mouseOutHandlerWrapper, false);
  canvas.addEventListener('mouseover', mouseOverHandlerWrapper, false);
  canvas.addEventListener('contextmenu', onContextMenu, false);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);

  WS.on({ event: 'lock_cursor', callback: onCursorLocked });
  WS.on({ event: 'unlock_cursor', callback: onCursorUnlocked });
};

export function removeStudioPointerEvents(canvas: HTMLCanvasElement) {
  canvas.removeEventListener('touchstart', studioTouchStartHandlerWrapper);
  canvas.removeEventListener('touchmove', studioTouchStartHandlerWrapper);
  canvas.removeEventListener('gesturestart', studioGectureStartHandlerWrapper);
  canvas.removeEventListener('gesturechange', studioGectureStartHandlerWrapper);
  canvas.removeEventListener('wheel', wheelHandlerWrapper);
  canvas.removeEventListener('mousedown', mouseDownHandlerWrapper);
  canvas.removeEventListener('mousemove', mouseMoveHandlerWrapper);
  canvas.removeEventListener('mouseup', mouseUpHandlerWrapper);
  canvas.removeEventListener('dblclick', doubleClickHandlerWrapper);
  canvas.removeEventListener('mouseout', mouseOutHandlerWrapper);
  canvas.removeEventListener('mouseover', mouseOverHandlerWrapper);
  canvas.removeEventListener('contextmenu', onContextMenu);
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('keyup', onKeyUp);

  WS.off({ event: 'lock_cursor', callback: onCursorLocked });
  WS.off({ event: 'unlock_cursor', callback: onCursorUnlocked });

  resetHelperVariables();
}
